import {
	Box,
	Button,
	Table,
	Text,
	Badge,
	Group,
	useMantineTheme,
	Skeleton,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { SessionContext } from "@/context/SessionContext";
import appSettings from "../appsettings.json";
import { Utils } from "../javascript/utils";
import {
	IconFile,
	IconDownload,
	IconMathGreater,
	IconMusic,
	IconCameraCheck,
	IconCameraSearch,
	IconCameraExclamation,
	IconCameraRotate,
	IconSalad,
} from "@tabler/icons-react";
import React, { useEffect, useState, useContext } from "react";
import { User } from "../javascript/user-objects";
import {
	InstrumentalistTask,
	ProjectSubmission,
} from "@/javascript/project-objects";
import ConductionAPI from "@/javascript/conduction-api";

const MyTasks: React.FC = () => {
	const [mySubmissions, setMySubmissions] = useState<ProjectSubmission[]>([]);
	const [expandedSubmissions, setExpandedSubmissions] = useState<string[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const theme = useMantineTheme();

	// check isMobile once, set text sizes accordingly
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
	const fzSmall = isMobile ? 10 : 14;
	const fzMed = isMobile ? 14 : 18;

	const HEADER_FW = 700;
	const TASKS_HEIGHT = isMobile ? 530 : 440;

	const [isUploading, setIsUploading] = useState<boolean>(false);

	const session = useContext(SessionContext);
	if (!session) {
		return null;
	}

	async function fetchBlob(url: string): Promise<Blob> {
		const res = await fetch(url, { cache: "force-cache" });
		if (!res.ok) throw new Error("Fetch failed");
		const blob = await res.blob();
		return blob;
	}

	function downloadBlob(url: string, filename: string) {
		fetchBlob(url)
			.then((blob) => {
				const blobUrl = window.URL.createObjectURL(blob);
				const link = document.createElement("a");
				link.href = blobUrl;
				link.download = filename;
				link.click();
				URL.revokeObjectURL(blobUrl);
			})
			.catch((error) => {
				console.error("Download error:", error);
			});

		return () => {};
	}

	const uploadFile = async (
		file: File,
		taskOrSubmissionID: string,
		isFeedback: boolean
	) => {
		const token = session.token;
		if (!token) return;
		try {
			setIsUploading(true);
			if (isFeedback) {
				await ConductionAPI.submitInstrumentalistFeedback(
					taskOrSubmissionID,
					file,
					token
				);
			} else {
				await ConductionAPI.submitInstrumentalistTask(
					taskOrSubmissionID,
					file,
					token
				);
			}
			Utils.displayNotification(
				"Video Submitted Successfully",
				"video file uploaded",
				"green",
				<IconCameraCheck size={fzMed} />
			);
			// refresh tasks
			// await updateVideoSubmit();
			session.fetchProjectSubmissions();
		} catch (error) {
			Utils.displayNotification(
				"Error: Video Not Submitted",
				"",
				"red",
				<IconSalad size={fzMed} />
			);
		} finally {
			setIsUploading(false);
		}
	};

	const createFilePicker = (taskOrSubmissionID: string, isFeedback: boolean) => {
		if (isUploading) return;
		const input = document.createElement("input");
		input.type = "file";
		input.accept = "video/*";
		input.onchange = (e) => {
			const file = (e.target as HTMLInputElement).files?.[0];
			if (file) {
				uploadFile(file, taskOrSubmissionID, isFeedback);
			}
		};
		input.click();
	};

	// Column widths for consistent spacing
	// sum(parent or child row %) = 100% - (due date %)
	const CW = {
		desktop: {
			parent: {
				dueDate: "18%",
				studentName: "18%",
				projectName: "18%",
				backingTrack: "18%",
				status: "16%",
				expand: "6%",
			},
			child: {
				indent: "18%",
				instrumentName: "22%",
				sheetMusic: "18%",
				upload: "42%",
			},
		},
		mobile: {
			parent: {
				dueDate: "20%",
				names: "20%",
				backingTrack: "20%",
				expand: "10%",
			},
			child: {
				indent: "0%",
				instrumentName: "25%",
				sheetMusic: "20%",
				upload: "50%",
			},
		},
	};

	// for mobile
	function getTruncatedString(s: string) {
		return s.length > 10 ? s.slice(0, 10) + "..." : s;
	}

	// multiple rows can be expanded at a time, all rows begin in the expanded state
	const toggleExpand = (submissionID: string) => {
		setExpandedSubmissions((prev) =>
			prev.includes(submissionID)
				? prev.filter((id) => id !== submissionID)
				: [...prev, submissionID]
		);
	};

	const subTasksComplete = (
		tasks: InstrumentalistTask[],
		feedbackProvider: User | null,
		feedbackDateFulfilled: Date | null
	) => {
		// filter by user tasks
		const myTasks = tasks.filter(
			(task) => task.instrumentalist.id === session.user?.id
		);
		const total =
			myTasks.length + (feedbackProvider?.id === session.user?.id ? 1 : 0);

		const completed =
			myTasks.filter((task) => task.dateFulfilled != null).length +
			(feedbackProvider?.id === session.user?.id && feedbackDateFulfilled ? 1 : 0);

		return (
			<Badge
				bg={completed === total ? theme.colors.green[8] : theme.colors.red[5]}
				size={"lg"}
				c={theme.colors.blue[0]}
			>
				{completed.toString()} / {total.toString()}
			</Badge>
		);
	};

	// Create skeleton loading rows
	const skeletonCell = <Skeleton height={20} ta="center" />;
	const skeletonRow = (
		<Table.Tr bg={theme.colors.blue[1]}>
			<Table.Td fz={fzSmall} w={"100%"} ta="center">
				{skeletonCell}
			</Table.Td>
			<Table.Td fz={fzSmall} w={"100%"} ta="center">
				{skeletonCell}
			</Table.Td>
			<Table.Td fz={fzSmall} w={"100%"} ta="center">
				{skeletonCell}
			</Table.Td>
			<Table.Td fz={fzSmall} w={"100%"} ta="center">
				{skeletonCell}
			</Table.Td>

			{!isMobile && (
				<Table.Td fz={fzSmall} w={"100%"} ta="center">
					{skeletonCell}
				</Table.Td>
			)}
			{!isMobile && (
				<Table.Td fz={fzSmall} w={"100%"} ta="center">
					{skeletonCell}
				</Table.Td>
			)}
		</Table.Tr>
	);

	const skeletonRows = Array.from({ length: 4 }, (_, i) =>
		React.cloneElement(skeletonRow, { key: `s${i}` })
	);

	useEffect(() => {
		setIsLoading(true);
		if (!session.projectSubmissions) {
			return;
		}
		setMySubmissions(
			session.projectSubmissions
				? session.projectSubmissions.filter(
						(submission) =>
							submission.dateFulfilled == null &&
							submission.instrumenatlistTasks.some(
								(task) => task.instrumentalist.id === session.user?.id
							)
				  )
				: []
		);
		setExpandedSubmissions(
			session.projectSubmissions?.map((submission) => submission.id) ?? []
		);
		setIsLoading(false);
	}, [session.projectSubmissions]);


	const rows = mySubmissions?.map((parentRow) => {
		const isExpanded = expandedSubmissions.includes(parentRow.id);
		return (
			<React.Fragment key={parentRow.id}>
				<Table.Tr
					onClick={() => toggleExpand(parentRow.id)}
					bg={theme.colors.blue[1]}
					style={{
						cursor: "pointer",
					}}
				>
					{/* Due Date */}
					<Table.Td
						fz={fzSmall}
						w={isMobile ? CW.mobile.parent.dueDate : CW.desktop.parent.dueDate}
						ta="center"
					>
						{new Date(parentRow.instrumentalistDueDate).toLocaleString()}
					</Table.Td>
					{isMobile ? (
						<>
							<Table.Td fz={fzSmall} w={CW.mobile.parent.names} ta="center">
								{getTruncatedString(parentRow.submissionProjectName || "N/A Project") +
									" by " +
									parentRow.submissionProjectName || "N/A User"}
							</Table.Td>
						</>
					) : (
						<>
							<Table.Td fz={fzSmall} w={CW.desktop.parent.studentName} ta="center">
								{parentRow.projectOwner?.username || "N/A User"}
							</Table.Td>
							<Table.Td fz={fzSmall} w={CW.desktop.parent.projectName} ta="center">
								{parentRow.submissionProjectName || "N/A Project"}
							</Table.Td>
						</>
					)}
					{/* mp3 Backing Track */}
					<Table.Td
						fz={fzSmall}
						w={
							isMobile ? CW.mobile.parent.backingTrack : CW.desktop.parent.backingTrack
						}
						ta="center"
					>
						{
							<Button
								variant="default"
								size="compact-sm"
								c={theme.colors.blue[8]}
								onClick={(e) => {
									const url = `${appSettings.S3BucketBaseUrl}InstrumentalistTaskFiles/${parentRow.id}.mp3`;
									// regex replace spaces with underscores. make prettier if project name has spaces
									const filename =
										`${parentRow.submissionProjectName}-BackingTrack.mp3`.replace(
											/ /g,
											"_"
										);
									downloadBlob(url, filename);
									Utils.displayNotification(
										"Downloaded " +
											(parentRow.submissionProjectName || "Project") +
											" Backing Track",
										"",
										"green",
										<IconDownload size={fzMed} />
									);
									e.stopPropagation();
									// don't expand the row if only the mp3 button is clicked
								}}
							>
								<IconMusic size={fzMed} />
								<IconDownload size={fzMed} />
							</Button>
						}
					</Table.Td>
					{/* Project Status */}
					{!isMobile && (
						<Table.Td fz={fzSmall} w={CW.desktop.parent.status} ta="center">
							{subTasksComplete(
								parentRow.instrumenatlistTasks,
								parentRow.feedbackProvider,
								parentRow.feedbackDateFulfilled
							)}
						</Table.Td>
					)}
					{/* Expand */}
					<Table.Td
						fz={fzSmall}
						w={isMobile ? CW.mobile.parent.expand : CW.desktop.parent.expand}
					>
						<Box ml={isMobile ? -10 : 0}>
							<IconMathGreater
								size={fzMed}
								style={{
									transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
								}}
							/>
						</Box>
					</Table.Td>
				</Table.Tr>
				{isExpanded && (
					<Table.Tr>
						<Table.Td colSpan={isMobile ? 4 : 6} p={0}>
							<Box
								style={{
									overflowX: "auto",
									width: "100%",
								}}
							>
								<Table w={"100%"}>
									<Table.Thead>
										<Table.Tr>
											<Table.Th
												w={isMobile ? CW.mobile.child.indent : CW.desktop.child.indent}
												ta="center"
											></Table.Th>
											<Table.Th
												w={
													isMobile
														? CW.mobile.child.instrumentName
														: CW.desktop.child.instrumentName
												}
												ta="center"
											>
												<Text fz={fzSmall} fw={HEADER_FW}>
													Instrument
												</Text>
											</Table.Th>
											<Table.Th
												w={
													isMobile ? CW.mobile.child.sheetMusic : CW.desktop.child.sheetMusic
												}
												ta="center"
												// only nowrap right now
												style={{ whiteSpace: "nowrap" }}
											>
												<Text fz={fzSmall} fw={HEADER_FW}>
													Sheet Music
												</Text>
											</Table.Th>
											<Table.Th
												w={isMobile ? CW.mobile.child.upload : CW.desktop.child.upload}
												ta="center"
											>
												<Text fz={fzSmall} fw={HEADER_FW}>
													Upload Video File
												</Text>
											</Table.Th>
										</Table.Tr>
									</Table.Thead>
									<Table.Tbody>
										{parentRow.instrumenatlistTasks
											.filter((task) => task.instrumentalist.id === session.user?.id)
											.map((childRow) => (
												// keeping task = child row
												<Table.Tr key={childRow.id}>
													{/* Indent */}
													<Table.Td
														fz={fzSmall}
														w={isMobile ? CW.mobile.child.indent : CW.desktop.child.indent}
														ta="center"
													>
														<Text fz={fzSmall * 0.9} c={theme.colors.blue[6]} fw={500}></Text>
													</Table.Td>
													{/* Instrument */}
													<Table.Td
														fz={fzSmall}
														w={
															isMobile
																? CW.mobile.child.instrumentName
																: CW.desktop.child.instrumentName
														}
														ta="center"
													>
														{isMobile
															? getTruncatedString(childRow.instrument.name)
															: childRow.instrument.name}
													</Table.Td>
													{/* Sheet Music */}
													<Table.Td
														fz={fzSmall}
														w={
															isMobile
																? CW.mobile.child.sheetMusic
																: CW.desktop.child.sheetMusic
														}
														ta="center"
													>
														<Button
															onClick={() => {
																const url = `${appSettings.S3BucketBaseUrl}InstrumentalistTaskFiles/${childRow.id}.pdf`;
																// regex replace spaces with underscores. instrument names have spaces
																const filename =
																	`${parentRow.submissionProjectName}-${childRow.instrument.name}-SheetMusic.pdf`.replace(
																		/ /g,
																		"_"
																	);
																downloadBlob(url, filename);
																Utils.displayNotification(
																	"Downloaded " +
																		(parentRow.submissionProjectName || "Project") +
																		" " +
																		childRow.instrument.name +
																		" Sheet Music",
																	"",
																	"green",
																	<IconDownload size={fzMed} />
																);
															}}
															variant="default"
															size="compact-sm"
															c={theme.colors.blue[8]}
														>
															<IconFile size={fzMed} />
															<IconDownload size={fzMed} />
														</Button>
													</Table.Td>
													{/* File Upload */}
													<Table.Td
														fz={fzSmall}
														w={isMobile ? CW.mobile.child.upload : CW.desktop.child.upload}
														ta="center"
													>
														{/* 'status bar' and submit button combined */}
														<Button
															fz={fzSmall}
															disabled={isUploading}
															onClick={() => {
																createFilePicker(childRow.id, false);
															}}
															variant="default"
															size="compact-sm"
															bg={
																childRow.dateFulfilled
																	? new Date(parentRow.instrumentalistDueDate) < new Date()
																		? theme.colors.yellow[1]
																		: theme.colors.green[1]
																	: new Date(parentRow.instrumentalistDueDate) < new Date()
																	? theme.colors.red[1]
																	: "white"
															}
														>
															{childRow.dateFulfilled ? (
																new Date(parentRow.instrumentalistDueDate) < new Date() ? (
																	<span
																		style={{ display: "flex", alignItems: "center", gap: "8px" }}
																	>
																		Task Completed <IconCameraCheck size={fzMed} />
																	</span>
																) : (
																	<span
																		style={{ display: "flex", alignItems: "center", gap: "8px" }}
																	>
																		Submit Again <IconCameraRotate size={fzMed} />
																	</span>
																)
															) : new Date(parentRow.instrumentalistDueDate) < new Date() ? (
																<span
																	style={{ display: "flex", alignItems: "center", gap: "8px" }}
																>
																	Past Due <IconCameraExclamation size={fzMed} />
																</span>
															) : (
																<span
																	style={{ display: "flex", alignItems: "center", gap: "8px" }}
																>
																	Not Yet Submitted <IconCameraSearch size={fzMed} />
																</span>
															)}
														</Button>
													</Table.Td>
												</Table.Tr>
											))}
										{/* feedback duty - check if user
										 has also been assigned feedback */}
										{parentRow.feedbackProvider?.id === session.user?.id && (
											<Table.Tr key={parentRow.id + "fb"}>
												{/* Indent */}
												<Table.Td
													fz={fzSmall}
													w={isMobile ? CW.mobile.child.indent : CW.desktop.child.indent}
													ta="center"
												>
													<Text fz={fzSmall * 0.9} c={theme.colors.blue[6]} fw={500}></Text>
												</Table.Td>
												{/* Instrument */}
												<Table.Td
													fz={fzSmall}
													w={
														isMobile
															? CW.mobile.child.instrumentName
															: CW.desktop.child.instrumentName
													}
													ta="center"
												>
													{isMobile ? getTruncatedString("Feedback Duty") : "Feedback Duty"}
												</Table.Td>
												{/* Sheet Music */}
												<Table.Td
													fz={fzSmall}
													w={
														isMobile
															? CW.mobile.child.sheetMusic
															: CW.desktop.child.sheetMusic
													}
													ta="center"
												></Table.Td>
												{/* File Upload */}
												<Table.Td
													fz={fzSmall}
													w={isMobile ? CW.mobile.child.upload : CW.desktop.child.upload}
													ta="center"
												>
													{/* 'status bar' and submit button combined */}
													<Button
														fz={fzSmall}
														disabled={isUploading}
														onClick={() => {
															createFilePicker(parentRow.id, true);
														}}
														variant="default"
														size="compact-sm"
														bg={
															parentRow.feedbackDateFulfilled
																? new Date(parentRow.instrumentalistDueDate) < new Date()
																	? theme.colors.yellow[1]
																	: theme.colors.green[1]
																: new Date(parentRow.instrumentalistDueDate) < new Date()
																? theme.colors.red[1]
																: "white"
														}
													>
														{parentRow.feedbackDateFulfilled ? (
															new Date(parentRow.instrumentalistDueDate) < new Date() ? (
																<span
																	style={{ display: "flex", alignItems: "center", gap: "8px" }}
																>
																	Task Completed <IconCameraCheck size={fzMed} />
																</span>
															) : (
																<span
																	style={{ display: "flex", alignItems: "center", gap: "8px" }}
																>
																	Submit Again <IconCameraRotate size={fzMed} />
																</span>
															)
														) : new Date(parentRow.instrumentalistDueDate) < new Date() ? (
															<span
																style={{ display: "flex", alignItems: "center", gap: "8px" }}
															>
																Past Due <IconCameraExclamation size={fzMed} />
															</span>
														) : (
															<span
																style={{ display: "flex", alignItems: "center", gap: "8px" }}
															>
																Not Yet Submitted <IconCameraSearch size={fzMed} />
															</span>
														)}
													</Button>
												</Table.Td>
											</Table.Tr>
										)}
									</Table.Tbody>
								</Table>
							</Box>
						</Table.Td>
					</Table.Tr>
				)}
			</React.Fragment>
		);
	});

	return (
		<Group>
			{!isMobile && (
				<Text fz={fzSmall} p={"auto"} c={theme.colors.blue[0]}>
					Submit your filmed performances here. You can resubmit until the video is
					in production. Expand / collapse rows to show / hide tasks associated with
					the same project.
				</Text>
			)}
			{isMobile && (
				<Text fz={fzSmall} p={"auto"} c={theme.colors.blue[0]}>
					Submit your filmed performances here.
				</Text>
			)}
			<Box
				w={"100%"}
				h={TASKS_HEIGHT}
				bg={theme.colors.blue[0]}
				style={{
					borderRadius: 16,
					boxSizing: "border-box",
					overflowX: "hidden",
				}}
			>
				<Table
					horizontalSpacing="md"
					verticalSpacing="xs"
					miw="auto"
					layout="fixed"
				>
					<Table.Thead>
						<Table.Tr>
							<Table.Th
								// width of "due date" column on desktop
								w={isMobile ? CW.mobile.parent.dueDate : CW.desktop.parent.dueDate}
								ta="center"
							>
								<Text fz={fzSmall} ta="center" fw={HEADER_FW}>
									Due Date
								</Text>
							</Table.Th>
							{isMobile ? (
								<Table.Th w={CW.desktop.parent.studentName} ta="center">
									<Text fz={fzSmall} fw={HEADER_FW}>
										User / Project
									</Text>
								</Table.Th>
							) : (
								<>
									<Table.Th w={CW.desktop.parent.projectName} ta="center">
										<Text fz={fzSmall} fw={HEADER_FW}>
											User Name
										</Text>
									</Table.Th>
									<Table.Th w={CW.desktop.parent.projectName} ta="center">
										<Text fz={fzSmall} fw={HEADER_FW}>
											Project Name
										</Text>
									</Table.Th>
								</>
							)}
							<Table.Th
								// width of backing track
								w={
									isMobile
										? CW.mobile.parent.backingTrack
										: CW.desktop.parent.backingTrack
								}
								ta="center"
							>
								<Text fz={fzSmall} fw={HEADER_FW}>
									Backing Track
								</Text>
							</Table.Th>
							{!isMobile && (
								<Table.Th w={CW.desktop.parent.status} ta="center">
									<Text fz={fzSmall} fw={HEADER_FW}>
										Tasks Complete
									</Text>
								</Table.Th>
							)}
							<Table.Th
								w={isMobile ? CW.mobile.parent.expand : CW.desktop.parent.expand}
								ta="center"
							>
								<Text fz={fzSmall} fw={500}></Text>
							</Table.Th>
						</Table.Tr>
					</Table.Thead>
					<Table.Tbody>
						{/* {skeletonRows} */}
						{isLoading ? (
							skeletonRows
						) : rows && rows.length > 0 ? (
							rows
						) : (
							<Table.Tr>
								<Table.Td colSpan={6}>
									<Text fw={HEADER_FW} ta="center">
										No Tasks
									</Text>
								</Table.Td>
							</Table.Tr>
						)}
					</Table.Tbody>
				</Table>
			</Box>
		</Group>
	);
};

export default MyTasks;
